import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from '@mui/material';
import { ReactNode } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import strings from 'constants/index';

type SelectFormFieldValue = { label: string; value: string | number };
type SelectFormFieldOnChange = (value: string | number, child?: ReactNode) => void;

interface SelectFormFieldProps
  extends Omit<SelectProps, 'onChange' | 'name' | 'defaultValue' | 'label'> {
  label?: string;
  required?: boolean;
  values: SelectFormFieldValue[];
  allowEmpty?: boolean;
  'data-testid'?: string;
  size?: 'small' | 'medium';
  onChange?: SelectFormFieldOnChange;
}

export function SelectFormField<T extends FieldValues>({
  name,
  label,
  control,
  required,
  rules,
  values,
  allowEmpty,
  defaultValue,
  size = 'medium',
  ...selectProps
}: UseControllerProps<T> & SelectFormFieldProps) {
  const { onChange, variant = 'standard', ...restSelectProps } = selectProps || {};
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue,
    rules: {
      required: { value: required, message: strings.field_is_required },
      ...rules,
    },
  });

  return (
    <FormControl
      variant={variant}
      fullWidth
      error={!!error}
      data-testid={name}
      required={required}
      size={size}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        {...field}
        {...restSelectProps}
        label={label}
        variant={variant}
        onChange={(e, child) => {
          const nextValue = e.target.value as string | number;
          field.onChange(nextValue);
          onChange?.(nextValue, child);
        }}
        value={field.value ?? (selectProps?.multiple ? [] : '')}
        renderValue={selected =>
          Array.isArray(selected) ? (
            <Box display="flex" flexWrap="wrap" gap="1px">
              {selected.map(value => (
                <Chip key={value} label={values.find(el => el.value === value)?.label} />
              ))}
            </Box>
          ) : (
            values.find(el => el.value === selected)?.label
          )
        }
      >
        <MenuItem value="" disabled={!allowEmpty}>
          <em>{strings.none}</em>
        </MenuItem>
        {values.map(({ value, label }) => (
          <MenuItem key={value} value={value} data-testid={label}>
            {label}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText title="select-error">{error.message}</FormHelperText>}
    </FormControl>
  );
}
